import React, { useRef, useEffect } from "react";
import logger from "utils/logger";

const prefix = "VIDEO:";

const Video = ({ stream, ...props }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const currentVideo = videoRef.current;
    let cleanupListener = null;

    if (stream) {
      currentVideo.srcObject = stream;

      if (props.autoPlay !== false) {
        // Safari webview requires explicit handling of play() promise
        const attemptPlay = (retryCount = 0) => {
          currentVideo.play().catch((error) => {
            // Safari webview sometimes needs a moment before it can play
            if (retryCount < 3) {
              setTimeout(() => {
                if (currentVideo.srcObject === stream) {
                  attemptPlay(retryCount + 1);
                }
              }, 100 * (retryCount + 1)); // Incremental delay: 100ms, 200ms, 300ms
            } else {
              logger.error(
                `${prefix} Video play failed after 3 attempts: ${error.name}`
              );
            }
          });
        };

        // Wait for metadata to be loaded before attempting play (important for Safari)
        if (currentVideo.readyState >= 1) {
          // Metadata already loaded
          attemptPlay();
        } else {
          // Safari webview sometimes doesn't fire loadedmetadata reliably
          // Set a timeout to detect this and notify parent to restart stream
          const metadataTimeout = setTimeout(() => {
            logger.error(
              `${prefix} Metadata timeout (readyState: ${currentVideo.readyState})`
            );

            // Notify parent component through onError callback if provided
            if (props.onError) {
              props.onError(
                new Error("Metadata loading timeout - stream may be broken")
              );
            } else {
              // Fallback: try to play anyway (might work in some cases)
              if (currentVideo.srcObject === stream) {
                attemptPlay();
              }
            }
          }, 1500); // Wait 1.5 seconds for metadata

          const onLoadedMetadata = () => {
            clearTimeout(metadataTimeout);
            attemptPlay();
          };

          currentVideo.addEventListener("loadedmetadata", onLoadedMetadata, {
            once: true,
          });
          cleanupListener = {
            listener: onLoadedMetadata,
            timeout: metadataTimeout,
          };
        }
      }
    } else {
      currentVideo.pause();
    }

    return () => {
      if (cleanupListener) {
        if (cleanupListener.listener) {
          // New format with timeout
          clearTimeout(cleanupListener.timeout);
          currentVideo.removeEventListener(
            "loadedmetadata",
            cleanupListener.listener
          );
        } else {
          // Old format (just the listener)
          currentVideo.removeEventListener("loadedmetadata", cleanupListener);
        }
      }
      if (currentVideo) {
        currentVideo.srcObject = null;
      }
      if (stream) {
        stream.getVideoTracks().forEach((track) => {
          track.stop();
        });
      }
    };
    // eslint-disable-next-line
  }, [stream, props.autoPlay]);

  useEffect(() => {
    const { current: videoElement } = videoRef;
    videoElement.setAttribute("muted", "");
  }, []);

  return <video playsInline muted autoPlay ref={videoRef} {...props} />;
};

export default React.memo(Video);
